import axios from 'axios';
import router from '@/router'; // Import your Vue Router instance
import store from '@/store';
import { useToast } from "vue-toastification"; // Import the toast function

const toast = useToast();

const getBaseUrl = () => {
  // Check if window.ENV exists (set by our runtime config)
  if (window.ENV && window.ENV.VUE_APP_API_BASE_URL) {
    return window.ENV.VUE_APP_API_BASE_URL;
  }
  // Fallback to the build-time environment variable
  return process.env.VUE_APP_API_BASE_URL || 'http://localhost:3000';
};

const apiClient = axios.create({
  baseURL: getBaseUrl(),
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add a request interceptor
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401 && error.config.url !== '/api/auth/login') {
      store.dispatch('logout');
      toast.error("Your session has expired. Please log in again.");
      router.push('/login');
    }
    return Promise.reject(error);
  }
);

console.log('API Base URL:', getBaseUrl());

// Function to lookup products
export const lookupProduct = async (query, page = 1, itemsPerPage = 5) => {
  const response = await apiClient.get('api/products', {
    params: {
      query: query,
      page: page,
      per_page: itemsPerPage
    }
  });
  return response;
};

// Function to save batch code information
export const saveBatchCode = (batchCodeData) => {
  if (batchCodeData._id) {
    // If the batch code has an ID, it's an update
    return apiClient.put(`/api/batch-codes/${batchCodeData._id}`, batchCodeData);
  } else {
    // If there's no ID, it's a new batch code
    return apiClient.post('/api/batch-codes', batchCodeData);
  }
};

// Function to search batch codes
export const searchBatchCodes = (query) => {
  return apiClient.get(`/api/batch-codes`, { params: { query } });
};

// Function to get a specific batch code by ID
export const getBatchCodesByProductId = (id) => {
  console.log('getBatchCodesByProductId',id);
  return apiClient.get(`/api/batch-codes/${id}`);
};

// Function to get all batch codes with pagination and search
export const getBatchCodes = async (params) => {
  const response = await apiClient.get('/api/batch-codes', { params });
  return response.data;
};

// Function to delete a batch code
export const deleteBatchCode = (id) => {
  return apiClient.delete(`/api/batch-codes/${id}`);
};

// Function to get packing list by order number
export const getPackingList = (orderNumberOrId) => {
  return apiClient.get(`/api/warehouse/packing-list/${orderNumberOrId}`);
};

// Upload files to the server
export const uploadFiles = (formData) => {
  return apiClient.post('/api/invoices/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
};

// New function to get processing updates
export const getProcessingUpdates = (onUpdate, onDone) => {
  const eventSource = new EventSource(`${getBaseUrl()}/api/invoices/processing-updates`);

  eventSource.onmessage = (event) => {
    try {
      if (event.data && event.data !== "undefined") {
        const data = JSON.parse(event.data);
        if (data.done) {
          eventSource.close();
          onDone();
        } else {
          onUpdate(data);
        }
      } else {
        console.warn('Received empty or undefined event data');
      }
    } catch (error) {
      console.error('Error parsing event data:', error);
    }
  };

  eventSource.onerror = (error) => {
    console.error('EventSource failed:', error);
    eventSource.close();
  };

  return () => {
    eventSource.close();
  };
};

// Function to get invoices with pagination, sorting, and filtering
export const getInvoices = (page, itemsPerPage, sortBy, sortDesc, query) => {
  const params = {
    page,
    itemsPerPage,
    sortBy,
    sortDesc,
    query,
  };
  return apiClient.get('/api/invoices', { params });
};

// Function to get a specific invoice by ID
export const getInvoiceById = (invoiceId) => {
  return apiClient.get(`/api/invoices/${invoiceId}`);
};

// Function to create a new invoice
export const createInvoice = (invoiceData) => {
  return apiClient.post('/api/invoices', invoiceData);
};

// Function to update an existing invoice
export const updateInvoice = (invoiceId, invoiceData) => {
  // Ensure that productLines are properly formatted
  const formattedInvoiceData = {
    ...invoiceData,
    productLines: invoiceData.productLines.map(line => ({
      ...line,
      price: line.newPrice || line.price // Use newPrice if it exists, otherwise use the original price
    }))
  };

  return apiClient.put(`/api/invoices/${invoiceId}`, formattedInvoiceData);
};

// Function to delete an invoice
export const deleteInvoice = (invoiceId) => {
  return apiClient.delete(`/api/invoices/${invoiceId}`);
};

// Add this function to fetch entities
export const getEntities = () => {
  return apiClient.get('/api/invoices/entities');
};

// Function to link a product to an invoice line
export const linkProductToInvoiceLine = (invoiceId, productName, productId, variantId) => {
  return apiClient.post(`/api/invoices/${invoiceId}/lines/${productName}/link-product`, { productId, variantId });
};

// New function to approve an invoice
export async function approveInvoice(invoiceId, approvalData) {
  try {
    const response = await apiClient.post(`/api/invoices/${invoiceId}/approve`, approvalData);
    return response.data;
  } catch (error) {
    console.error('Error approving invoice:', error);
    throw error;
  }
}

// Function to get BOMs with search functionality
export const getBOMs = async ({ search = '', page = 1, limit = 10 }) => {
  const response = await apiClient.get('/api/bom', { 
    params: { search, page, limit }
  });
  return response.data;
};

export const getBOMById = async (id) => {
  const response = await apiClient.get(`/api/bom/${id}`);
  return response.data;
};

// Function to create a new BOM
export const createBOM = (bomData) => {
  console.log('Creating BOM:', bomData);
  return apiClient.post('/api/bom', bomData);
};

// Function to update an existing BOM
export const updateBOM = (bomId, bomData) => {
  return apiClient.put(`/api/bom/${bomId}`, bomData);
};

// Function to delete a BOM
export const deleteBOM = (bomId) => {
  return apiClient.delete(`/api/bom/${bomId}`);
};

// Add these functions to the existing apiClient.js file

export const getManufacturingOrders = async ({ search = '', page = 1, itemsPerPage = 10 }) => {
  return await apiClient.get('/api/manufacturing-orders', {
    params: { search, page, itemsPerPage }
  });
};

export const createManufacturingOrder = async (orderData) => {
  return await apiClient.post('/api/manufacturing-orders', orderData);
};

export const updateManufacturingOrder = async (orderId, orderData) => {
  return await apiClient.put(`/api/manufacturing-orders/${orderId}`, orderData);
};

export async function updateManufacturingOrderStatus(orderId, newStatus) {
  try {
    const response = await apiClient.patch(`/api/manufacturing-orders/${orderId}/status`, { status: newStatus });
    return response.data;
  } catch (error) {
    console.error('Error updating manufacturing order status:', error);
    throw error;
  }
}

export const deleteManufacturingOrder = async (orderId) => {
  return await apiClient.delete(`/api/manufacturing-orders/${orderId}`);
};

export const getWarehouses = async () => {
  return await apiClient.get('/api/warehouse');
};

// Manufacturing Planning Recommendations (per Veeqo warehouse)
export const getManufactureRecommendations = async ({ warehouseId, warehouseIds, startDate, endDate, targetDays = 30, batchSize }) => {
  const params = {};
  
  // Support both single warehouseId and multiple warehouseIds
  if (warehouseIds) {
    params.warehouseIds = Array.isArray(warehouseIds) ? warehouseIds.join(',') : warehouseIds;
  } else if (warehouseId) {
    params.warehouseId = warehouseId;
  }
  
  params.startDate = startDate;
  params.endDate = endDate;
  params.targetDays = targetDays;
  if (batchSize) params.batchSize = batchSize;
  
  const response = await apiClient.get('/api/manufacturing-planning/recommendations', { params });
  return response.data;
};

// Manufacturing Planning Dashboard Stats
export const getManufacturingDashboardStats = async (params) => {
  // params should include: warehouseIds (comma-separated or array), startDate, endDate, targetDays
  const response = await apiClient.get('/api/manufacturing-planning/dashboard-stats', { params });
  return response.data;
};

// Manufacturing Planning Alerts
export const getManufacturingAlerts = async (params) => {
  // params should include: warehouseIds (comma-separated or array), startDate, endDate, targetDays, leadTime
  const response = await apiClient.get('/api/manufacturing-planning/alerts', { params });
  return response.data;
};

// Manufacturing Planning Smart Allocation
export const getManufacturingSmartAllocation = async (params) => {
  // params should include: warehouseIds (comma-separated or array), startDate, endDate
  const response = await apiClient.get('/api/manufacturing-planning/smart-allocation', { params });
  return response.data;
};



export const getPurchaseOrders = async (params) => {
  try {
    const response = await apiClient.get('/api/purchase-orders', { params });
    return response.data || { purchaseOrders: [], total: 0 };
  } catch (error) {
    console.error('Error fetching purchase orders:', error);
    throw error;
  }
};

export const diagnosePurchaseOrder = async (number) => {
  try {
    const encoded = encodeURIComponent(number.trim());
    const response = await apiClient.get(`/api/purchase-orders/diagnose/${encoded}`);
    return response.data;
  } catch (error) {
    console.error('Error diagnosing purchase order:', error);
    throw error;
  }
};

export const fixCorruptPurchaseOrderLines = async (number) => {
  try {
    const encoded = encodeURIComponent(number.trim());
    const response = await apiClient.post(`/api/purchase-orders/diagnose/${encoded}/fix`);
    return response.data;
  } catch (error) {
    console.error('Error fixing corrupt purchase order lines:', error);
    throw error;
  }
};

export const createPurchaseOrderInvoice = async (purchaseOrderId) => {
  return await apiClient.post(`/api/purchase-orders/${purchaseOrderId}/create-invoice`);
};

export const downloadPurchaseOrderPDF = async (purchaseOrderId, purchaseOrderNumber) => {
  try {
    const response = await apiClient.get(`/api/purchase-orders/${purchaseOrderId}/pdf`, {
      responseType: 'blob',
    });
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = `purchase_order_${purchaseOrderNumber}.pdf`;
    link.click();
    window.URL.revokeObjectURL(link.href);
  } catch (error) {
    console.error('Error downloading purchase order PDF:', error);
    throw error;
  }
};

export const createSalesOrderFromPO = async (purchaseOrderId) => {
  try {
    const response = await apiClient.post(`/api/purchase-orders/${purchaseOrderId}/create-sales-order`);
    return response.data;
  } catch (error) {
    console.error('Error creating sales order from PO:', error);
    throw error;
  }
};

export const getOESalesOrders = async (params) => {
  try {
    const response = await apiClient.get('/api/purchase-orders/oe-sales-orders', { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching OE sales orders:', error);
    throw error;
  }
};

export const createManufacturingOrdersFromSO = async (purchaseOrderId, orderData) => {
  try {
    const response = await apiClient.post(`/api/purchase-orders/${purchaseOrderId}/create-manufacturing-orders`, orderData);
    return response.data;
  } catch (error) {
    console.error('Error creating manufacturing orders from SO:', error);
    throw error;
  }
};

export const createInvoicesForOESalesOrder = async (purchaseOrderId) => {
  try {
    const response = await apiClient.post(`/api/purchase-orders/${purchaseOrderId}/create-invoices`);
    return response.data;
  } catch (error) {
    console.error('Error creating invoices for OE sales order:', error);
    throw error;
  }
};

//user management
export const getUsers = async ({ search = '', page = 1, limit = 10 }) => {
  console.log('Getting users with search:', search);
  return await apiClient.get('/api/auth/users', { params: { search, page, limit } });
};

export const createUser = async (userData) => {
  return await apiClient.post('/api/auth/register', userData);
};

export const updateUserStatus = async (userId, status) => {
  return await apiClient.patch(`/api/auth/users/${userId}/status`, { status });
};

export const resetUserPassword = async (userId) => {
  return await apiClient.post(`/api/auth/admin-reset-password/${userId}`);
};

// User-initiated password reset
export const requestPasswordReset = async (email) => {
  return await apiClient.post('/api/auth/reset-password', { email });
};

export const updateUser = async (userId, userData) => {
  return await apiClient.put(`/api/auth/users/${userId}`, userData);
};

// SKU codes
export const generateSKUCode = async (skuData) => {
  return await apiClient.post('/api/products/sku-codes', skuData);
};

// Product management
export const getMissingSkuProducts = async (entityId, params) => {
  return await apiClient.get(`/api/products/missing-skus/${entityId}`, { params });
};

export const updateProductSkus = async (entityId, products) => {
  return await apiClient.post(`/api/products/update-skus/${entityId}`, { products });
};

// Unified: get products with missing fields from selected sources
export const getMissingFieldProducts = async (entityId, { page = 1, itemsPerPage = 10, search = '', fields = ['sku'], sources = ['shopify'] }) => {
  const params = {
    page,
    itemsPerPage,
    search,
    fields: Array.isArray(fields) ? fields.join(',') : fields,
    sources: Array.isArray(sources) ? sources.join(',') : sources
  };
  return await apiClient.get(`/api/products/missing-fields/${entityId}`, { params });
};

export const updateMissingFields = async (entityId, updates) => {
  return await apiClient.post(`/api/products/update-missing/${entityId}`, { updates });
};

export async function getSuppliers() {
  return await apiClient.get('/api/products/suppliers');
}

export async function getCategories(entityId) {
  return await apiClient.post('/api/products/categories', { entityId });
}

// Stock take

export const getStockTakes = async ({ search = '', page = 1, itemsPerPage = 10 }) => {
  try {
    const response = await apiClient.get('/api/stock-takes', {
      params: { search, page, itemsPerPage }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching stock takes:', error);
    throw error;
  }
};

export const getStockTakeById = async (id) => {
  try {
    const response = await apiClient.get(`/api/stock-takes/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching stock take:', error);
    throw error;
  }
};

export const createStockTake = async (stockTakeData) => {
  try {
    const response = await apiClient.post('/api/stock-takes', stockTakeData);
    return response.data;
  } catch (error) {
    console.error('Error creating stock take:', error);
    throw error;
  }
};

export const getLocations = async (entityId) => {
  return await apiClient.post('/api/stock-takes/locations', { entityId });
};

export const getAllLocations = async () => {
  return await apiClient.get('/api/stock-takes/all-locations');
};

export const getBrands = async (entityId) => {
  return await apiClient.post('/api/products/brands', { entityId });
};

export const updateStockTake = async (id, stockTakeData) => {
  try {
    const response = await apiClient.put(`/api/stock-takes/${id}`, stockTakeData);
    return response.data;
  } catch (error) {
    console.error('Error updating stock take:', error);
    throw error;
  }
};

export const deleteStockTake = async (id) => {
  try {
    const response = await apiClient.delete(`/api/stock-takes/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting stock take:', error);
    throw error;
  }
};

export const getStockTakeSummary = async (stockTakeId) => {
  try {
    const response = await apiClient.get(`/api/stock-takes/${stockTakeId}/summary`);
    return response.data;
  } catch (error) {
    console.error('Error fetching stock take summary:', error);
    throw error;
  }
};
export const updateStockTakeStatus = async (stockTakeId, newStatus) => {
  const response = await apiClient.put(`/api/stock-takes/${stockTakeId}/status`, { status: newStatus });
  return response.data;
};

// Function to download stock sheet PDF
export const downloadStockSheetPdf = async (stockTakeId) => {
  return apiClient.get(`/api/stock-takes/${stockTakeId}/generate-stock-sheet`, {
    responseType: 'blob',
  });
};

// Add this new function
export const updateStockTakeItemCount = async (stockTakeId, itemUpdate) => {
  try {
    const response = await apiClient.put(`/api/stock-takes/${stockTakeId}/item-count`, itemUpdate);
    console.log('updateStockTakeItemCount response', JSON.stringify(response,null,2));
    return response.data;
  } catch (error) {
    console.error('Error updating stock take item count:', error);
    throw error;
  }
};

export const getInventorySnapshots = (params) => {
  return apiClient.get('/api/inventory-snapshots', { params });
};

export const getInventorySnapshotDetails = (id) => {
  return apiClient.get(`/api/inventory-snapshots/${id}/details`);
};

export const createInventorySnapshot = (data) => {
  return apiClient.post('/api/inventory-snapshots', data);
};

export const deleteInventorySnapshot = (id) => {
  return apiClient.delete(`/api/inventory-snapshots/${id}`);
};

export const downloadInventorySnapshot = async (id) => {
  try {
    const response = await apiClient.get(`/api/inventory-snapshots/${id}/download`, {
      responseType: 'blob'
    });
    
    // Get the filename from the Content-Disposition header
    console.log('response headers:', JSON.stringify(response.headers));
    const contentDisposition = response.headers['content-disposition'];
    let filename = 'inventory_snapshot.xlsx';
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i);
      if (filenameMatch && filenameMatch.length === 2) {
        filename = filenameMatch[1].replace(/['"]/g, ''); // Remove any quotes
      }
    }

    // If we couldn't get the filename from the header, generate one based on the current date
    if (filename === 'inventory_snapshot.xlsx') {
      const currentDate = new Date().toISOString().split('T')[0];
      filename = `inventory_snapshot_${currentDate}.xlsx`;
    }

    const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = filename;
    link.click();
    window.URL.revokeObjectURL(link.href);
  } catch (error) {
    console.error('Error downloading inventory snapshot:', error);
    throw error;
  }
};

//logistics reconciliation
export const getInvoicesForReconciliation = async (params) => {
  return await apiClient.get('/api/logistics/reconciliation', { params });
};

export const generateInvoiceCrossCharges = async (invoiceId, account, channel, invoiceAccount, invoiceChannel) => {
  try {
    const response = await apiClient.post(`/api/logistics/reconciliation/${invoiceId}/generate-cross-charges`, {
      account,
      channel,
      invoiceAccount,
      invoiceChannel
    });
    return response;  // The response should include the updated invoice data
  } catch (error) {
    // If the server sends a specific error message, pass it through
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    // Otherwise, throw the original error
    throw error;
  }
};

//upload invoices
export const uploadInvoice = async (formData) => {
  return await apiClient.post('/api/logistics/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
};

// Add this new function to your apiClient.js
export const deleteLogisticsInvoice = async (invoiceId) => {
  return await apiClient.delete(`/api/logistics/invoices/${invoiceId}`);
};

// Add this new function to get shipping providers
export const getShippingProviders = async () => {
  return await apiClient.get('/api/logistics/providers');
};

//wholesale orders
export const getWholesaleOrders = async ({ search = '', page = 1, limit = 10 }) => {
  try {
    const response = await apiClient.get('/api/wholesale-orders', {
      params: { search, page, limit }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching wholesale orders:', error);
    throw error;
  }
};

export const createWholesaleOrderInvoice = async (orderId, data) => {
  try {
    const response = await apiClient.post(`/api/wholesale-orders/${orderId}/create-invoice`, data);
    return response;
  } catch (error) {
    console.error('Error creating wholesale order invoice:', error);
    throw error;
  }
};

export const getPractitioners = () => {
  return apiClient.get('/api/practitioners');
};

export const createPractitioner = (practitionerData) => {
  return apiClient.post('/api/practitioners', practitionerData);
};

export const updatePractitioner = (id, practitionerData) => {
  return apiClient.put(`/api/practitioners/${id}`, practitionerData);
};

export const getPractitionerCommissions = (practitionerId) => {
  return apiClient.get(`/api/practitioners/${practitionerId}/commission-payments`);
};

export const getCommissionPaymentDetails = (practitionerId, paymentId) => {
  return apiClient.get(`/api/practitioners/${practitionerId}/commission-payments/${paymentId}`);
};

export const updatePractitionerPaymentStatus = async (practitionerId, paymentId, status) => {
  return await apiClient.put(`/api/practitioners/${practitionerId}/commission-payments/${paymentId}/status`, {
    status
  });
};

export const downloadSummaryReport = async (stockTakeId) => {
  try {
    const response = await apiClient.get(`/api/stock-takes/${stockTakeId}/summary-report`, {
      responseType: 'blob'
    });
    
    // Create a download link
    const blob = new Blob([response.data], { 
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `stock_take_summary_${stockTakeId}.xlsx`;
    
    // Trigger download
    document.body.appendChild(link);
    link.click();
    
    // Cleanup
    window.URL.revokeObjectURL(url);
    document.body.removeChild(link);
  } catch (error) {
    console.error('Error downloading summary report:', error);
    throw error;
  }
};

export const createConsignmentOrder = async (stockTakeId) => {
  try {
    const response = await apiClient.post(`/api/stock-takes/${stockTakeId}/consignment-order`);
    return response.data;
  } catch (error) {
    console.error('Error creating consignment order:', error);
    throw error;
  }
};

export const getLocationById = async (locationId) => {
  try {
    const response = await apiClient.get(`/api/stock-takes/location/${locationId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching location:', error);
    throw error;
  }
};

export const getStockMovementReports = async ({ page = 1, itemsPerPage = 10, search = '' }) => {
  const response = await apiClient.get('/api/stock-movement/reports', {
    params: {
      page: {
        page,
        itemsPerPage,
        search
      }
    }
  });
  return response.data;
};

export const createStockMovementReport = async (formData) => {
  return await apiClient.post('/api/stock-movement/reports', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
};

export const downloadStockMovementReport = async (reportId) => {
  try {
    const response = await apiClient.get(`/api/stock-movement/reports/${reportId}/download`, {
      responseType: 'blob',
      headers: {
        Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      }
    });
    
    // Check if we got an error response
    if (response.data.type && response.data.type.includes('application/json')) {
      // Convert blob to text to read error message
      const text = await response.data.text();
      const error = JSON.parse(text);
      throw new Error(error.message || 'Download failed');
    }
    
    return response.data;
  } catch (error) {
    console.error('Error downloading stock movement report:', error);
    throw error;
  }
};

export const getManufacturingOrderById = async (id) => {
  return await apiClient.get(`/api/manufacturing-orders/${id}`);
};

// Add these new functions for AI verification
export const startAIVerification = async (stockTakeId) => {
  try {
    const response = await apiClient.post(`/api/stock-takes/${stockTakeId}/verify`);
    return response.data;
  } catch (error) {
    console.error('Error starting AI verification:', error);
    throw error;
  }
};

export const sendMessageToAI = async (stockTakeId, message) => {
  try {
    const response = await apiClient.post(`/api/stock-takes/${stockTakeId}/ai-message`, { message });
    return response.data;
  } catch (error) {
    console.error('Error sending message to AI:', error);
    throw error;
  }
};

// Helper function to get auth token
const getAuthToken = () => {
  return localStorage.getItem('token');
};

// AI Assistant endpoints
export const getAIAssistants = () => {
    return apiClient.get('/api/ai-assistants');
};

export const getAIAssistant = (id) => {
    return apiClient.get(`/api/ai-assistants/${id}`);
};

export const createAIAssistant = (assistantData) => {
    return apiClient.post('/api/ai-assistants', assistantData);
};

export const updateAIAssistant = (id, assistantData) => {
    return apiClient.put(`/api/ai-assistants/${id}`, assistantData);
};

export const reinitializeAIAssistant = (id) => {
  return apiClient.post(`/api/ai-assistants/${id}/reinitialize`);
};

export const getAssistantFiles = (assistantId) => {
    return apiClient.get(`/api/ai-assistants/${assistantId}/files`);
};

export const uploadAssistantFile = (formData, assistantId) => {
    return apiClient.post(`/api/ai-assistants/${assistantId}/files/upload`, formData, {
        headers: {
            'Content-Type': 'multipart/form-data'
        }
    });
};

export const deleteAssistantFile = (assistantId, fileId) => {
    return apiClient.delete(`/api/ai-assistants/${assistantId}/files/${fileId}`);
};

export const getDispensaryProducts = async (params) => {
    return await apiClient.get('/api/dispensary/products', { params });
};

export const getDispensaryLocations = async () => {
    return await apiClient.get('/api/dispensary/locations');
};

// Dispensary Manufacturing endpoints
export const startManufacturing = async (productId, manufacturingData) => {
  try {
    const response = await apiClient.post(
      `/api/dispensary-manufacturing/products/${productId}/manufacture`, 
      manufacturingData
    );
    return response.data;
  } catch (error) {
    console.error('\n apiClient.js:', error);
    throw error;
  }
};

export const completeVariantManufacturing = async (productId, manufacturingId, variantData) => {
  try {
    const response = await apiClient.post(
      `/api/dispensary-manufacturing/products/${productId}/manufacture/${manufacturingId}/complete-variant`,
      variantData
    );
    return response.data;
  } catch (error) {
    console.error('Error completing variant manufacturing:', error);
    throw error;
  }
};

export const completeAllManufacturing = async (productId, manufacturingId) => {
  try {
    const response = await apiClient.post(
      `/api/dispensary-manufacturing/products/${productId}/manufacture/${manufacturingId}/complete`
    );
    return response.data;
  } catch (error) {
    console.error('Error completing manufacturing:', error);
    throw error;
  }
};

export const getProductBOMs = async (productId) => {
  try {
    const response = await apiClient.get(`/api/dispensary/products/${productId}/used-in-boms`);
    return response.data;
  } catch (error) {
    console.error('Error fetching product BOMs:', error);
    throw error;
  }
};

export const getComponentStockLevels = async (locationName, components) => {
  try {
    console.log('getComponentStockLevels called with:', {
      locationName,
      components
    });
    const response = await apiClient.post('/api/dispensary/components/stock', {
      locationName,
      components
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching component stock levels:', error);
    throw error;
  }
};

export const getProductStockWithBuffer = async (productId, variantId, locationName) => {
  try {
    const response = await apiClient.get('/api/dispensary/product-stock', {
      params: {
        productId,
        variantId,
        locationName
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching product stock with buffer:', error);
    throw error;
  }
};

export const getManufacturingPlanningData = async (startDate, endDate, locationName, orderStatus = 'shipped') => {
  try {
    const response = await apiClient.get('/api/dispensary/manufacturing-planning', {
      params: {
        startDate,
        endDate,
        locationName,
        orderStatus
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error getting manufacturing planning data:', error);
    throw error;
  }
};

export const createPlannedManufacturingOrders = async (manufacturingItems, locationName) => {
  try {
    const response = await apiClient.post('/api/dispensary/manufacturing-planning/create', {
      manufacturingItems,
      locationName
    });
    return response.data;
  } catch (error) {
    console.error('Error creating planned manufacturing orders:', error);
    throw error;
  }
};

// Add new function to check for in-progress manufacturing
export const getInProgressManufacturing = async (productId, locationName) => {
  try {
    const response = await apiClient.get(
      `/api/dispensary-manufacturing/products/${productId}/in-progress`,
      { params: { locationName } }
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching in-progress manufacturing:', error);
    throw error;
  }
};

export const generateProductBOMs = async (productTypes) => {
  return await apiClient.post('/api/bom/generate', { productTypes });
};

export const getBatchCodesAIInsights = async () => {
  const response = await apiClient.get('/api/batch-codes/insights/ai');
  return response.data;
};

export const getDispensaryAIInsights = async (type) => {
    const response = await apiClient.get('/api/dispensary/insights/ai', { params: { type } });
    return response.data;
};

// Prescription API Functions
export const getPrescriptions = async (params) => {
  const response = await apiClient.get('/api/prescriptions', { params });
  return response.data;
};

export const getPrescription = async (id) => {
  const response = await apiClient.get(`/api/prescriptions/${id}`);
  return response.data;
};

export const createPrescription = async (prescriptionData) => {
  const response = await apiClient.post('/api/prescriptions', prescriptionData);
  return response.data;
};

export const updatePrescription = async (id, prescriptionData) => {
  const response = await apiClient.put(`/api/prescriptions/${id}`, prescriptionData);
  return response.data;
};

export const deletePrescription = async (id) => {
  const response = await apiClient.delete(`/api/prescriptions/${id}`);
  return response.data;
};

export const getCollectionLocations = async () => {
  const response = await apiClient.get('/api/prescriptions/collection-locations');
  return response.data;
};

export const getPrescriptionProducts = async (type) => {
  const response = await apiClient.get('/api/prescriptions/products', { params: { type } });
  return response.data;
};

// Add this with the other prescription functions
export const emailPrescription = async (prescriptionId) => {
  try {
    const response = await apiClient.post(`/api/prescriptions/${prescriptionId}/email`);
    return response.data;
  } catch (error) {
    console.error('Error sending prescription email:', error);
    throw error;
  }
};

// AI Insights endpoints
export const getAIInsights = () => {
  return apiClient.get('/api/ai-insights');
};

export const getAIInsightDetails = (insightId) => {
  return apiClient.get(`/api/ai-insights/${insightId}/data`);
};

export const interactWithInsight = (insightId, message) => {
  return apiClient.post(`/api/ai-insights/${insightId}/interact`, { message });
};

export const createAIInsight = (insightData) => {
  return apiClient.post('/api/ai-insights', insightData);
};

export const updateAIInsight = (insightId, insightData) => {
  return apiClient.put(`/api/ai-insights/${insightId}`, insightData);
};

// Add this new function with the other AI Insights endpoints
export const getAIInsightData = async (insightId) => {
  try {
    const response = await apiClient.get(`/api/ai-insights/${insightId}/data`);
    return response.data;
  } catch (error) {
    console.error('Error fetching insight data:', error);
    throw error;
  }
};

export const startInsightAnalysis = async (insightId) => {
  try {
    const response = await apiClient.post(`/api/ai-insights/${insightId}/analyze`);
    return response;
  } catch (error) {
    console.error('Error starting insight analysis:', error);
    throw error;
  }
};

export const downloadPractitionerCommissions = async (practitionerId, { startDate, endDate } = {}) => {
  return apiClient.get(`/api/practitioners/${practitionerId}/commissions/download`, {
    params: { startDate, endDate },
    responseType: 'arraybuffer'
  });
};

export const getBonusProducts = () => {
  return apiClient.get('/api/practitioners/bonus-products');
};

export const createBonusProduct = (productData) => {
  return apiClient.post('/api/practitioners/bonus-products', productData);
};

export const updateBonusProduct = (id, productData) => {
  return apiClient.put(`/api/practitioners/bonus-products/${id}`, productData);
};

export const deleteBonusProduct = (id) => {
  return apiClient.delete(`/api/practitioners/bonus-products/${id}`);
};

export const getDashboardData = () => {
  return apiClient.get('/api/practitioners/dashboard');
};

// Add these functions for handling conflicts
export const getOrderTagConflicts = () => {
  return apiClient.get('/api/practitioners/conflicts/tags');
};

export const markOrderConflictResolved = (orderNumber) => {
  // Remove any '#' from the order number if present
  const cleanOrderNumber = orderNumber.replace('#', '');
  console.log('Resolving conflict for order:', cleanOrderNumber);
  return apiClient.put(`/api/practitioners/conflicts/tags/${cleanOrderNumber}/resolve`);
};

export const verifyPin = async (prescriptionId, pin, role) => {
  const response = await apiClient.post('/api/prescriptions/verify-pin', { prescriptionId, pin, role });
  return response.data;
};

// Function to update PIN
export const updateUserPin = async (newPin, currentPin = null) => {
  try {
    const response = await apiClient.post('/api/auth/update-pin', {
      newPin,
      currentPin
    });
    return response.data;
  } catch (error) {
    console.error('Error updating PIN:', error);
    throw error;
  }
};

export const fulfillPrescription = async (formData) => {
  try {
    const response = await apiClient.post('/api/prescriptions/fulfill', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error fulfilling prescription:', error);
    throw error;
  }
};

export const downloadPrescriptionLabel = async (prescriptionId) => {
  return await apiClient.get(`/api/prescriptions/${prescriptionId}/label`, {
    responseType: 'arraybuffer'
  });
};

// Store Agent endpoints
export const createStoreAgent = (agentData) => {
  return apiClient.post('/api/store-agents', agentData);
};

export const updateStoreAgent = (agentId, agentData) => {
  return apiClient.put(`/api/store-agents/${agentId}`, agentData);
};

export const getStoreAgents = () => {
  return apiClient.get('/api/store-agents');
};

export const getStoreAgentActivity = (agentId) => {
  return apiClient.get(`/api/store-agents/${agentId}/activity`);
};

export const getPendingApprovals = (agentId) => {
  return apiClient.get(`/api/store-agents/${agentId}/pending-approvals`);
};

export const approveAgentAction = (approvalId, approved) => {
  return apiClient.post(`/api/store-agents/approvals/${approvalId}`, { approved });
};

export const getAgentReports = (agentId) => {
  return apiClient.get(`/api/store-agents/${agentId}/reports`);
};

export const downloadAgentReport = async (reportId) => {
  const response = await apiClient.get(`/api/store-agents/reports/${reportId}/download`, {
    responseType: 'blob'
  });
  
  const blob = new Blob([response.data], { type: 'application/pdf' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `agent-report-${reportId}.pdf`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

export default apiClient;

export const getSkills = () => {
  return apiClient.get('/api/skills');
};

export const createSkill = (skillData) => {
  return apiClient.post('/api/skills', skillData);
};

export const updateSkill = (skillId, skillData) => {
  return apiClient.put(`/api/skills/${skillId}`, skillData);
};

export const deleteSkill = (skillId) => {
  return apiClient.delete(`/api/skills/${skillId}`);
};

export const getSkill = (skillId) => {
  return apiClient.get(`/api/skills/${skillId}`);
};

export const updateAgentSkill = (agentId, skillId, config) => {
  return apiClient.patch(`/api/agents/${agentId}/skills/${skillId}`, config);
};

export const addSkillToAgent = (agentId, skillId) => {
  return apiClient.post(`/api/agents/${agentId}/skills`, { skillId });
};

// Add this with the other prescription-related functions
export const emailPractitioner = async (prescriptionId, message) => {
  try {
    const response = await apiClient.post(`/api/prescriptions/${prescriptionId}/email-practitioner`, {
      message
    });
    return response.data;
  } catch (error) {
    console.error('Error sending practitioner email:', error);
    throw error;
  }
};

// Add this with other prescription-related functions
export const duplicatePrescription = async (prescriptionId) => {
  try {
    const response = await apiClient.post(`/api/prescriptions/${prescriptionId}/duplicate`);
    return response.data;
  } catch (error) {
    console.error('Error duplicating prescription:', error);
    throw error;
  }
};

// Add this new function with the other prescription-related functions
export const getPrescriptionHistory = async (prescriptionId, repeatNumber) => {
  try {
    const response = await apiClient.get(`/api/prescriptions/history/${prescriptionId}`, {
      params: { repeatNumber }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching prescription history:', error);
    throw error;
  }
};

// Add new function to create a group of prescriptions
export const createPrescriptionGroup = async (prescriptions) => {
  try {
    const response = await apiClient.post('/api/prescriptions/group', { prescriptions });
    return response.data;
  } catch (error) {
    console.error('Error creating prescription group:', error);
    throw error;
  }
};

export const addAdditionalProducts = async (prescriptionId, additionalProducts) => {
  try {
    const response = await apiClient.post(`/api/prescriptions/${prescriptionId}/additional-products`, {
      additionalProducts
    });
    return response.data;
  } catch (error) {
    console.error('Error adding additional products:', error);
    throw error;
  }
};

// Practitioner referral endpoints
export const getReferredPractitioners = (practitionerId) => {
  return apiClient.get(`/api/practitioners/${practitionerId}/referred`);
};

export const getReferralStats = (practitionerId) => {
  return apiClient.get(`/api/practitioners/${practitionerId}/referral-stats`);
};

export const joinWithReferralCode = (practitionerData) => {
  return apiClient.post('/api/practitioners/join', practitionerData);
};

// Add these new functions (the others already exist)
export const searchPractitioners = async (query) => {
  try {
    const response = await apiClient.get('/api/practitioners/search', {
      params: { query }
    });
    return response.data;
  } catch (error) {
    console.error('Error searching practitioners:', error);
    throw error;
  }
};

export const addReferredPractitioner = (referringPractitionerId, practitionerId) => {
  return apiClient.post('/api/practitioners/add-referral', {
    referringPractitionerId,
    practitionerId
  });
};

export const getIntercompanyTransfers = async ({ search = '', page = 1, itemsPerPage = 10 }) => {
  try {
    const response = await apiClient.get('/api/intercompany-transfers', {
      params: { search, page, itemsPerPage }
    });
    return response;
  } catch (error) {
    console.error('Error fetching intercompany transfers:', error);
    throw error;
  }
};

export const getIntercompanyTransferById = async (id) => {
  try {
    const response = await apiClient.get(`/api/intercompany-transfers/${id}`);
    return response;
  } catch (error) {
    console.error('Error fetching intercompany transfer details:', error);
    throw error;
  }
};

export const createIntercompanyTransfer = async (transferData) => {
  try {
    const response = await apiClient.post('/api/intercompany-transfers', transferData);
    return response;
  } catch (error) {
    console.error('Error creating intercompany transfer:', error);
    throw error;
  }
};

export const updateIntercompanyTransfer = async (transferId, transferData) => {
  try {
    const response = await apiClient.put(`/api/intercompany-transfers/${transferId}`, transferData);
    return response;
  } catch (error) {
    console.error('Error updating intercompany transfer:', error);
    throw error;
  }
};

export const updateIntercompanyTransferStatus = async (transferId, newStatus) => {
  try {
    console.log(`API request: updateIntercompanyTransferStatus - ID: ${transferId}, Status: ${newStatus}`);
    
    // Log the exact request payload
    const payload = { status: newStatus };
    console.log('Request payload:', JSON.stringify(payload));
    
    // Log auth token presence (but not the token itself)
    const hasToken = !!localStorage.getItem('token');
    console.log('Auth token present:', hasToken);
    
    const response = await apiClient.patch(`/api/intercompany-transfers/${transferId}/status`, payload);
    
    console.log('Status update response:', response.data);
    return response;
  } catch (error) {
    console.error('Error updating intercompany transfer status:', error);
    
    // Enhanced error logging
    if (error.response) {
      console.error('Response Status:', error.response.status);
      console.error('Response Headers:', JSON.stringify(error.response.headers));
      console.error('Response Data:', JSON.stringify(error.response.data));
      console.error('Request URL:', error.config?.url);
      console.error('Request Method:', error.config?.method);
      console.error('Request Data:', JSON.stringify(error.config?.data));
    } else if (error.request) {
      // The request was made but no response was received
      console.error('No response received:', error.request);
    } else {
      // Something happened in setting up the request
      console.error('Request setup error:', error.message);
    }
    
    throw error;
  }
};

export const deleteIntercompanyTransfer = async (transferId) => {
  try {
    const response = await apiClient.delete(`/api/intercompany-transfers/${transferId}`);
    return response;
  } catch (error) {
    console.error('Error deleting intercompany transfer:', error);
    throw error;
  }
};

// Xero Integration Functions
export const getXeroStatus = async () => {
  try {
    const response = await apiClient.get('/api/xero/status');
    return response.data;
  } catch (error) {
    console.error('Error checking Xero status:', error);
    throw error;
  }
};

export const refreshXeroTokens = async () => {
  try {
    const response = await apiClient.post('/api/xero/refresh');
    return response.data;
  } catch (error) {
    console.error('Error refreshing Xero tokens:', error);
    throw error;
  }
};

export const initiateXeroConnect = async (forceReconnect = false) => {
  try {
    // Get the base URL from our API config for the server
    const baseUrl = getBaseUrl();
    
    // Return absolute URL to the server endpoint
    return `${baseUrl}/api/xero/connect${forceReconnect ? '?force=true' : ''}`;
  } catch (error) {
    console.error('Error preparing Xero connection URL:', error);
    throw error;
  }
};

export const createAutomaticTransfers = async () => {
  try {
    const response = await apiClient.post('/api/intercompany-transfers/auto-create');
    return response.data;
  } catch (error) {
    console.error('Error creating automatic transfers:', error);
    throw error;
  }
};

// Cross-Company Reconciliation
export const generateCrossCompanyReconciliation = async (params) => {
  return await apiClient.post('/api/cross-company/reconciliation', params);
};

export const getCrossCompanyReconciliations = async (params) => {
  return await apiClient.get('/api/cross-company/reconciliation', { params });
};

export const approveCrossCompanyReconciliation = async (reconciliationId, approvalData) => {
  return await apiClient.post(`/api/cross-company/reconciliation/${reconciliationId}/approve`, approvalData);
};

export const deleteCrossCompanyReconciliation = async (reconciliationId) => {
  return await apiClient.delete(`/api/cross-company/reconciliation/${reconciliationId}`);
};

// Add this API function with the other Cross-Company Reconciliation functions
export const removeOrderFromReconciliation = async (reconciliationId, orderId) => {
  return await apiClient.delete(`/api/cross-company/reconciliation/${reconciliationId}/orders/${orderId}`);
};

// Add this function with the other Cross-Company Reconciliation functions, 
// or update it if it already exists
export const getReconciliationById = async (reconciliationId) => {
  return await apiClient.get(`/api/cross-company/reconciliation/${reconciliationId}`);
};

// Add this new function with the other Cross-Company Reconciliation functions
export const downloadCrossCompanyReconciliation = async (reconciliationId) => {
  try {
    const response = await apiClient.get(`/api/cross-company/reconciliation/${reconciliationId}/download`, {
      responseType: 'blob'
    });
    
    // Get the filename from the Content-Disposition header
    const contentDisposition = response.headers['content-disposition'];
    let filename = 'cross_company_reconciliation.xlsx';
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i);
      if (filenameMatch && filenameMatch.length === 2) {
        filename = filenameMatch[1].replace(/['"]/g, ''); // Remove any quotes
      }
    }

    // If we couldn't get the filename from the header, generate one with date
    if (filename === 'cross_company_reconciliation.xlsx') {
      const currentDate = new Date().toISOString().split('T')[0];
      filename = `cross_company_reconciliation_${currentDate}.xlsx`;
    }

    const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = filename;
    link.click();
    window.URL.revokeObjectURL(link.href);
  } catch (error) {
    console.error('Error downloading cross-company reconciliation:', error);
    throw error;
  }
};

export const updateStockTakeItemReason = async (stockTakeId, itemData) => {
  return apiClient.put(`/api/stock-takes/${stockTakeId}/item-reason`, itemData);
};

export const getPendingManufacturingOrders = async (locationName) => {
  try {
    const response = await apiClient.get('/api/dispensary/manufacturing-planning/pending', {
      params: { locationName }
    });
    return response.data;
  } catch (error) {
    console.error('Error getting pending manufacturing orders:', error);
    throw error;
  }
};

export const completeBatchManufacturingOrders = async (sessionId, locationName) => {
  try {
    const response = await apiClient.post('/api/dispensary/manufacturing-planning/complete-batch', {
      sessionId,
      locationName
    });
    return response.data;
  } catch (error) {
    console.error('Error completing batch manufacturing orders:', error);
    throw error;
  }
};

// ===== FAIRE API METHODS =====

// Test Faire API connection
export const testFaireConnection = async () => {
  try {
    const response = await apiClient.post('/api/faire/test-connection');
    return response.data;
  } catch (error) {
    console.error('Error testing Faire connection:', error);
    
    // If authentication is required, indicate API key is needed
    if (error.response?.status === 401 && error.response?.data?.requiresAuth) {
      return {
        success: false,
        message: 'Please set FAIRE_API_KEY environment variable to connect.',
        requiresAuth: true
      };
    }
    
    throw error;
  }
};

// Sync orders from Faire API
export const syncFaireOrders = async (options = {}) => {
  try {
    const response = await apiClient.post('/api/faire/sync/orders', options);
    return response.data;
  } catch (error) {
    console.error('Error syncing Faire orders:', error);
    throw error;
  }
};

export const syncAllFaireOrders = async () => {
  try {
    const response = await apiClient.post('/api/faire/sync/all-orders', {});
    return response.data;
  } catch (error) {
    console.error('Error syncing all Faire orders:', error);
    throw error;
  }
};

// Get Faire analytics
export const getFaireAnalytics = async (options = {}) => {
  try {
    const response = await apiClient.get('/api/faire/analytics', {
      params: options
    });
    return response.data;
  } catch (error) {
    console.error('Error getting Faire analytics:', error);
    throw error;
  }
};

// Get Faire product analytics
export const getFaireProductAnalytics = async (params) => {
  try {
    const response = await apiClient.get('/api/faire/analytics/products', { params });
    return response.data;
  } catch (error) {
    console.error('Error getting Faire product analytics:', error);
    throw error;
  }
};

// Get Faire customer analytics
export const getFaireCustomerAnalytics = async (params) => {
  try {
    const response = await apiClient.get('/api/faire/analytics/customers', { params });
    return response.data;
  } catch (error) {
    console.error('Error getting Faire customer analytics:', error);
    throw error;
  }
};

// Get Faire orders
export const getFaireOrders = async (params) => {
  try {
    const response = await apiClient.get('/api/faire/orders', { params });
    return response.data;
  } catch (error) {
    console.error('Error getting Faire orders:', error);
    throw error;
  }
};

// Get Faire order by ID
export const getFaireOrderById = async (orderId) => {
  try {
    const response = await apiClient.get(`/api/faire/orders/${orderId}`);
    return response.data;
  } catch (error) {
    console.error('Error getting Faire order:', error);
    throw error;
  }
};

// Geocode customer addresses
export const geocodeFaireCustomerAddresses = async (limit = 100) => {
  try {
    const response = await apiClient.post('/api/faire/geocode/addresses', {}, {
      params: { limit }
    });
    return response.data;
  } catch (error) {
    console.error('Error geocoding customer addresses:', error);
    throw error;
  }
};

// Get customer location data for map
export const getFaireCustomerLocations = async (options = {}) => {
  try {
    const response = await apiClient.get('/api/faire/customer-locations', {
      params: options
    });
    return response.data;
  } catch (error) {
    console.error('Error getting customer locations:', error);
    throw error;
  }
};

// Get AI recommendations for specific insights
export const getFaireAIRecommendations = async (insightData, analyticsData) => {
  try {
    const response = await apiClient.post('/api/faire/ai-recommendations', {
      insight: insightData,
      analytics: analyticsData
    });
    return response.data;
  } catch (error) {
    console.error('Error getting AI recommendations:', error);
    throw error;
  }
};

// Get streaming URL for AI recommendations
export const getFaireAIRecommendationsStreamUrl = (insightData, analyticsData) => {
  const token = getAuthToken();
  const baseUrl = getBaseUrl();
  return `${baseUrl}/api/faire/ai-recommendations/stream?token=${token}&insight=${encodeURIComponent(JSON.stringify(insightData))}&analytics=${encodeURIComponent(JSON.stringify(analyticsData))}`;
};

// Note: Faire API now uses simple API key authentication via FAIRE_API_KEY environment variable
// OAuth methods have been removed in favor of simplified authentication

// ===== PIF MANAGEMENT API METHODS =====

// Get all PIFs with pagination and search
export const getPIFs = async ({ search = '', page = 1, limit = 10 }) => {
  try {
    const response = await apiClient.get('/api/pif', {
      params: { search, page, limit }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching PIFs:', error);
    throw error;
  }
};

// Get single PIF by ID
export const getPIFById = async (id) => {
  try {
    const response = await apiClient.get(`/api/pif/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching PIF:', error);
    throw error;
  }
};

// Create new PIF
export const createPIF = async (pifData) => {
  try {
    const response = await apiClient.post('/api/pif', pifData);
    return response.data;
  } catch (error) {
    console.error('Error creating PIF:', error);
    throw error;
  }
};

// Update PIF
export const updatePIF = async (id, pifData) => {
  try {
    const response = await apiClient.put(`/api/pif/${id}`, pifData);
    return response.data;
  } catch (error) {
    console.error('Error updating PIF:', error);
    throw error;
  }
};

// Delete PIF
export const deletePIF = async (id) => {
  try {
    const response = await apiClient.delete(`/api/pif/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting PIF:', error);
    throw error;
  }
};

// Link product to PIF
export const linkProductToPIF = async (pifId, productId, variantId) => {
  try {
    const response = await apiClient.post(`/api/pif/${pifId}/link-product`, {
      productId,
      variantId
    });
    return response.data;
  } catch (error) {
    console.error('Error linking product to PIF:', error);
    throw error;
  }
};

// Upload artwork file
export const uploadPIFArtwork = async (pifId, formData) => {
  try {
    const response = await apiClient.post(`/api/pif/${pifId}/artwork`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error uploading artwork:', error);
    throw error;
  }
};

// Delete artwork file
export const deletePIFArtwork = async (pifId, fileId) => {
  try {
    const response = await apiClient.delete(`/api/pif/${pifId}/artwork/${fileId}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting artwork:', error);
    throw error;
  }
};

// Upload certification file
export const uploadPIFCertification = async (pifId, formData) => {
  try {
    const response = await apiClient.post(`/api/pif/${pifId}/certification`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error uploading certification:', error);
    throw error;
  }
};

// Delete certification file
export const deletePIFCertification = async (pifId, fileId) => {
  try {
    const response = await apiClient.delete(`/api/pif/${pifId}/certification/${fileId}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting certification:', error);
    throw error;
  }
};

// Download file
export const downloadPIFFile = async (pifId, fileId, filename) => {
  try {
    const response = await apiClient.get(`/api/pif/${pifId}/download/${fileId}`, {
      responseType: 'blob'
    });
    
    const blob = new Blob([response.data]);
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = filename;
    link.click();
    window.URL.revokeObjectURL(link.href);
  } catch (error) {
    console.error('Error downloading file:', error);
    throw error;
  }
};

// Create new INCI version
export const createPIFInciVersion = async (pifId, inciData) => {
  try {
    const response = await apiClient.post(`/api/pif/${pifId}/inci-version`, inciData);
    return response.data;
  } catch (error) {
    console.error('Error creating INCI version:', error);
    throw error;
  }
};

// Get INCI version history
export const getPIFInciHistory = async (pifId) => {
  try {
    const response = await apiClient.get(`/api/pif/${pifId}/inci-history`);
    return response.data;
  } catch (error) {
    console.error('Error fetching INCI history:', error);
    throw error;
  }
};

// Upload INCI file (docx, csv, txt)
export const uploadPIFInci = async (pifId, formData) => {
  try {
    const response = await apiClient.post(`/api/pif/${pifId}/inci-upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error uploading INCI file:', error);
    throw error;
  }
};

// ===== REPORTS API METHODS =====

// Get top products report with sales and profit breakdown
export const getTopProductsReport = async ({ startDate, endDate, limit = 25, sortBy = 'revenue', comparePriorPeriod = false }) => {
  try {
    const response = await apiClient.get('/api/reports/top-products', {
      params: { startDate, endDate, limit, sortBy, comparePriorPeriod }
    });
    return response.data;
  } catch (error) {
    console.error('Error getting top products report:', error);
    throw error;
  }
};

export const getSlowMovingProductsReport = async ({ startDate, endDate, threshold = 5, sortBy = 'daysOfStock' }) => {
  try {
    const response = await apiClient.get('/api/reports/slow-moving-products', {
      params: { startDate, endDate, threshold, sortBy }
    });
    return response.data;
  } catch (error) {
    console.error('Error getting slow moving products report:', error);
    throw error;
  }
};

// ===== COST PRICE MANAGEMENT API METHODS =====

// Upload cost price Excel file
export const uploadCostPrices = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return apiClient.post('/api/cost-prices/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};

// Get all cost price records with pagination
export const getCostPrices = (params) => {
  return apiClient.get('/api/cost-prices', { params });
};

// Get cost price history for a specific SKU
export const getCostPriceHistory = (skuCode) => {
  return apiClient.get(`/api/cost-prices/${skuCode}/history`);
};

// Update a single cost price record
export const updateCostPrice = (id, data) => {
  return apiClient.put(`/api/cost-prices/${id}`, data);
};

// Delete a cost price record
export const deleteCostPrice = (id) => {
  return apiClient.delete(`/api/cost-prices/${id}`);
};

// ===== Kit/Bundle Management =====

// Get all kits with cost calculations
export const getKits = (params) => {
  return apiClient.get('/api/kits', { params });
};

// Get specific kit by ID
export const getKitById = (kitId) => {
  return apiClient.get(`/api/kits/${kitId}`);
};

// Export kits to Excel
export const exportKitsToExcel = () => {
  return apiClient.get('/api/kits/export/excel', { 
    responseType: 'blob' 
  });
};

// ===== Internal Purchase Orders =====

// Get all internal purchase orders
export const getInternalPurchaseOrders = async (params) => {
  try {
    const response = await apiClient.get('/api/internal-purchase-orders', { params });
    return response.data || { purchaseOrders: [], totalItems: 0 };
  } catch (error) {
    console.error('Error fetching internal purchase orders:', error);
    throw error;
  }
};

// Get internal purchase order by ID
export const getInternalPurchaseOrderById = async (id) => {
  const response = await apiClient.get(`/api/internal-purchase-orders/${id}`);
  return response.data;
};

// Create internal purchase order
export const createInternalPurchaseOrder = async (data) => {
  const response = await apiClient.post('/api/internal-purchase-orders', data);
  return response.data;
};

// Update internal purchase order
export const updateInternalPurchaseOrder = async (id, data) => {
  const response = await apiClient.put(`/api/internal-purchase-orders/${id}`, data);
  return response.data;
};

// Delete/cancel internal purchase order
export const deleteInternalPurchaseOrder = async (id) => {
  const response = await apiClient.delete(`/api/internal-purchase-orders/${id}`);
  return response.data;
};

// Approve internal purchase order
export const approveInternalPurchaseOrder = async (id) => {
  const response = await apiClient.post(`/api/internal-purchase-orders/${id}/approve`);
  return response.data;
};

// Send internal purchase order
export const sendInternalPurchaseOrder = async (id) => {
  const response = await apiClient.post(`/api/internal-purchase-orders/${id}/send`);
  return response.data;
};

// Create sales order in Veeqo (Optimised Energetics internal POs only)
export const createInternalPOSalesOrder = async (id) => {
  const response = await apiClient.post(`/api/internal-purchase-orders/${id}/create-sales-order`);
  return response.data;
};

// Receive goods for internal purchase order
export const receiveInternalPurchaseOrderGoods = async (id, data) => {
  const response = await apiClient.post(`/api/internal-purchase-orders/${id}/receive`, data);
  return response.data;
};

// Close internal purchase order
export const closeInternalPurchaseOrder = async (id) => {
  const response = await apiClient.post(`/api/internal-purchase-orders/${id}/close`);
  return response.data;
};

// Parse document with AI for internal purchase order
export const parseInternalPODocument = async (formData) => {
  const response = await apiClient.post('/api/internal-purchase-orders/parse', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
  return response.data;
};

// Download internal purchase order PDF
export const downloadInternalPurchaseOrderPDF = async (id, poNumber) => {
  try {
    const response = await apiClient.get(`/api/internal-purchase-orders/${id}/pdf`, {
      responseType: 'blob'
    });
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = `${poNumber}.pdf`;
    link.click();
    window.URL.revokeObjectURL(link.href);
  } catch (error) {
    console.error('Error downloading PDF:', error);
    throw error;
  }
};

// Get suppliers for internal purchase orders
export const getInternalPOSuppliers = async () => {
  const response = await apiClient.get('/api/internal-purchase-orders/suppliers');
  return response.data;
};

// Get entities for internal purchase orders
export const getInternalPOEntities = async () => {
  const response = await apiClient.get('/api/internal-purchase-orders/entities');
  return response.data;
};

// Search Veeqo products (for PO line items)
export const searchVeeqoProducts = async (query) => {
  const response = await apiClient.get('/api/products', {
    params: { query, per_page: 20 }
  });
  return response.data;
};

// Get all warehouse locations (for PO receiving)
export const getWarehouseLocations = async () => {
  const response = await apiClient.get('/api/warehouse/locations');
  return response.data;
};

// ==========================================
// Customer Sales Orders API
// ==========================================

// Get customer sales orders with pagination and filters
export const getCustomerSalesOrders = async (params) => {
  try {
    const response = await apiClient.get('/api/customer-sales-orders', { params });
    return response.data || { salesOrders: [], totalItems: 0 };
  } catch (error) {
    console.error('Error fetching customer sales orders:', error);
    throw error;
  }
};

// Get customer sales order by ID
export const getCustomerSalesOrderById = async (id) => {
  const response = await apiClient.get(`/api/customer-sales-orders/${id}`);
  return response.data;
};

// Create a new customer sales order
export const createCustomerSalesOrder = async (data) => {
  const response = await apiClient.post('/api/customer-sales-orders', data);
  return response.data;
};

// Update a customer sales order
export const updateCustomerSalesOrder = async (id, data) => {
  const response = await apiClient.put(`/api/customer-sales-orders/${id}`, data);
  return response.data;
};

// Delete/Cancel a customer sales order
export const deleteCustomerSalesOrder = async (id) => {
  const response = await apiClient.delete(`/api/customer-sales-orders/${id}`);
  return response.data;
};

// Parse a customer PO document with AI
export const parseCustomerSODocument = async (formData) => {
  const response = await apiClient.post('/api/customer-sales-orders/parse', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return response.data;
};

// Send customer sales order to Veeqo
export const sendCustomerSOToVeeqo = async (id) => {
  const response = await apiClient.post(`/api/customer-sales-orders/${id}/send-to-veeqo`);
  return response.data;
};

// Send customer sales order to Xero
export const sendCustomerSOToXero = async (id) => {
  const response = await apiClient.post(`/api/customer-sales-orders/${id}/send-to-xero`);
  return response.data;
};

// Mark customer sales order as complete
export const completeCustomerSalesOrder = async (id) => {
  const response = await apiClient.post(`/api/customer-sales-orders/${id}/complete`);
  return response.data;
};

// Get customers for customer sales orders (searches Veeqo and local)
export const getCustomerSOCustomers = async (query) => {
  const response = await apiClient.get('/api/customer-sales-orders/customers', {
    params: { query }
  });
  return response.data;
};

// Get entities for customer sales orders
export const getCustomerSOEntities = async () => {
  const response = await apiClient.get('/api/customer-sales-orders/entities');
  return response.data;
};

// Search products by SKU for customer sales orders
export const searchCustomerSOProducts = async (sku) => {
  const response = await apiClient.get('/api/customer-sales-orders/search-products', {
    params: { sku }
  });
  return response.data;
};

// ===== VEEQO ORDERS EXPLORER API METHODS =====

// Get Veeqo orders with pagination and filters
export const getVeeqoOrders = async (params) => {
  try {
    const response = await apiClient.get('/api/orders', { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching Veeqo orders:', error);
    throw error;
  }
};

// Get single Veeqo order by ID
export const getVeeqoOrderById = async (id) => {
  try {
    const response = await apiClient.get(`/api/orders/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching Veeqo order:', error);
    throw error;
  }
};

// Get filter options for Veeqo orders (statuses, channels, entities)
export const getVeeqoOrderFilters = async () => {
  try {
    const response = await apiClient.get('/api/orders/filters');
    return response.data;
  } catch (error) {
    console.error('Error fetching Veeqo order filters:', error);
    throw error;
  }
};

// Export Veeqo orders to Excel
export const exportVeeqoOrders = async (params) => {
  try {
    const response = await apiClient.get('/api/orders/export', {
      params,
      responseType: 'blob'
    });
    
    // Get filename from Content-Disposition header
    const contentDisposition = response.headers['content-disposition'];
    let filename = 'veeqo-orders-export.xlsx';
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i);
      if (filenameMatch && filenameMatch.length === 2) {
        filename = filenameMatch[1].replace(/['"]/g, '');
      }
    }
    
    // Create blob and download
    const blob = new Blob([response.data], { 
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
    });
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = filename;
    link.click();
    window.URL.revokeObjectURL(link.href);
    
    return { success: true };
  } catch (error) {
    console.error('Error exporting Veeqo orders:', error);
    throw error;
  }
};

// Export Veeqo orders with line items (one row per line item)
export const exportVeeqoOrdersDetailed = async (params) => {
  try {
    const response = await apiClient.get('/api/orders/export-detailed', {
      params,
      responseType: 'blob'
    });
    
    // Get filename from Content-Disposition header
    const contentDisposition = response.headers['content-disposition'];
    let filename = 'veeqo-orders-detailed.xlsx';
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i);
      if (filenameMatch && filenameMatch.length === 2) {
        filename = filenameMatch[1].replace(/['"]/g, '');
      }
    }
    
    // Create blob and download
    const blob = new Blob([response.data], { 
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
    });
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = filename;
    link.click();
    window.URL.revokeObjectURL(link.href);
    
    return { success: true };
  } catch (error) {
    console.error('Error exporting detailed Veeqo orders:', error);
    throw error;
  }
};


